<!--
  通用居中弹窗
  - 内容（图片 + 文案）走 children 插槽，调用方自己决定有没有图、图多大
  - 按钮通过 prop 控制：showCancel 决定一个还是两个按钮，文案和回调都可传
  用法：
    <CenterDialog show={x} showCancel onCancel={..} onConfirm={..}>
      <img src={successIcon} class="w-[160px] h-[135px] mx-auto" alt="" />
      <p class="dlg-text">已登记并领取成功</p>
    </CenterDialog>
-->
<script lang="ts">
  import type { Snippet } from "svelte";
  import { onDestroy } from "svelte";
  import { _ as t } from "svelte-i18n";
  import { portal } from "svelte-portal";
  import { lockScroll, unlockScroll } from "../lib/scroll-lock";

  interface Props {
    show: boolean;
    /** 是否显示取消按钮（false = 只有一个确认按钮） */
    showCancel?: boolean;
    /** 取消 / 确认按钮文案，默认取 i18n 的 btn_cancel / btn_confirm */
    cancelText?: string;
    confirmText?: string;
    /** 点击取消，不传则走 onClose */
    onCancel?: () => void;
    /** 点击确认，不传则走 onClose */
    onConfirm?: () => void;
    /** 关闭（点遮罩、或取消/确认没传回调时兜底） */
    onClose?: () => void;
    /** 点遮罩是否关闭，默认不关 */
    closeOnMask?: boolean;
    /** 图片 + 文案 */
    children?: Snippet;
  }

  let {
    show,
    showCancel = false,
    cancelText,
    confirmText,
    onCancel,
    onConfirm,
    onClose,
    closeOnMask = false,
    children,
  }: Props = $props();

  // 跟踪当前实例是否持有锁，用于 onDestroy 兜底
  let hasLock = false;

  // 打开时锁住背景滚动，使用全局引用计数器
  $effect(() => {
    if (!show) return;
    lockScroll();
    hasLock = true;
    return () => {
      unlockScroll();
      hasLock = false;
    };
  });

  // 兜底：组件被意外销毁时释放锁
  onDestroy(() => {
    if (hasLock) {
      unlockScroll();
      hasLock = false;
    }
  });

  function handleMask() {
    if (closeOnMask) onClose?.();
  }

  function handleCancel() {
    if (onCancel) onCancel();
    else onClose?.();
  }

  function handleConfirm() {
    if (onConfirm) onConfirm();
    else onClose?.();
  }
</script>

{#if show}
  <!--
    portal 到 body：页面滚动容器是 NavBar 的 .nav-content，
    fixed 遮罩留在滚动容器里关闭时会有残影闪一下
  -->
  <div
    class="cd-mask"
    use:portal
    role="presentation"
    onclick={handleMask}
  >
    <!-- 阻止点击内容时冒泡到遮罩 -->
    <div
      class="cd-card"
      role="presentation"
      onclick={(e) => e.stopPropagation()}
    >
      {#if children}
        <div class="cd-content">
          {@render children()}
        </div>
      {/if}

      <div class="cd-actions" class:is-single={!showCancel}>
        {#if showCancel}
          <button type="button" class="cd-btn is-cancel" onclick={handleCancel}>
            {cancelText ?? $t("btn_cancel")}
          </button>
        {/if}
        <button type="button" class="cd-btn is-confirm" onclick={handleConfirm}>
          {confirmText ?? $t("btn_confirm")}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .cd-mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 210;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    background: rgba(0, 0, 0, 0.5);
  }

  .cd-card {
    width: 295px;
    max-width: 100%;
    padding: 20px;
    border-radius: 16px;
    background: #ffffff;
  }

  .cd-content {
    text-align: center;
  }

  /* 按钮上下间距固定 20px，调用方不用管 */
  .cd-actions {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }

  .cd-btn {
    flex: 1;
    min-width: 0;
    height: 44px;
    border-radius: 22px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
  }

  /* 取消：浅灰底深色字 */
  .cd-btn.is-cancel {
    background: #f2f4f7;
    color: #1d1d1d;
  }

  /* 确认：纯色 #2E4CE4（不用渐变描边那套） */
  .cd-btn.is-confirm {
    background: #2e4ce4;
    color: #ffffff;
  }
</style>
